import { useEffect, useState, useCallback } from "react";
import { api, downloadFile, formatApiError, queuedMessage } from "@/lib/api";
import Layout from "@/components/Layout";
import ConfirmDelete from "@/components/ConfirmDelete";
import { useAuth } from "@/context/AuthContext";
import { Plus, Download, Edit3, X, ArrowDownToLine, ArrowUpFromLine, Settings, Trash2 } from "lucide-react";
import { toast, Toaster } from "sonner";

const todayStr = () => new Date().toISOString().slice(0, 10);
const emptyForm = () => ({
  type: "inward", date: todayStr(), voucher_no: "", voucher_date: "",
  party: "", category: "", item_id: "", qty: 0, price: 0, gst_pct: 0,
  inward_person: "", remarks: "",
});

export default function InwardOutward() {
  const { user, isSuper, canSeePrices } = useAuth();
  const isOp = !isSuper && user?.role !== "admin";
  const isAdmin = isSuper || user?.role === "admin";

  const [items, setItems] = useState([]);
  const [filterType, setFilterType] = useState("");
  const [categories, setCategories] = useState([]);
  const [parties, setParties] = useState([]);
  const [persons, setPersons] = useState([]);
  const [invItems, setInvItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [editing, setEditing] = useState(null);
  const [remark, setRemark] = useState("");
  const [form, setForm] = useState(emptyForm());
  const [coilRows, setCoilRows] = useState([{ coil_no: "", weight_kg: "" }]);
  const [activeCoils, setActiveCoils] = useState([]);
  const [issueSel, setIssueSel] = useState({}); // coil_id -> issue weight

  const selItem = invItems.find((it) => it.id === form.item_id);
  const isCoilItem = !!selItem?.is_coil;

  const load = useCallback(() => api.get("/inward-outward", { params: filterType ? { type: filterType } : {} }).then((r) => setItems(r.data)), [filterType]);
  const loadMasters = useCallback(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/inout-parties").then((r) => setParties(r.data)).catch(() => {});
    api.get("/inout-persons").then((r) => setPersons(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadMasters(); }, [loadMasters]);

  // Load inventory items for the selected category
  useEffect(() => {
    if (!showForm) return;
    api.get("/inout-items", { params: form.category ? { category: form.category } : {} }).then((r) => setInvItems(r.data)).catch(() => setInvItems([]));
  }, [form.category, showForm]);

  // For coil-item OUTWARD, load the active coils to pick from
  useEffect(() => {
    if (showForm && isCoilItem && form.type === "outward" && form.item_id) {
      api.get(`/inout-coils/${form.item_id}`).then((r) => setActiveCoils(r.data)).catch(() => setActiveCoils([]));
    } else { setActiveCoils([]); }
  }, [showForm, isCoilItem, form.type, form.item_id]);

  const coilTotalWeight = isCoilItem
    ? (form.type === "inward"
        ? coilRows.reduce((s, c) => s + (+c.weight_kg || 0), 0)
        : Object.values(issueSel).reduce((s, w) => s + (+w || 0), 0))
    : (+form.qty || 0);
  const base = (isCoilItem ? coilTotalWeight : (+form.qty || 0)) * (+form.price || 0);
  const gstAmt = base * (+form.gst_pct || 0) / 100;
  const totalVal = base + gstAmt;

  const startNew = () => { setEditing(null); setRemark(""); setForm(emptyForm()); setCoilRows([{ coil_no: "", weight_kg: "" }]); setIssueSel({}); setShowForm(true); };
  const startEdit = (g) => {
    setEditing(g); setRemark("");
    setForm({ ...emptyForm(), ...g, qty: g.qty, price: g.price || 0, gst_pct: g.gst_pct || 0 });
    setCoilRows((g.coils && g.coils.length) ? g.coils.map((c) => ({ coil_no: c.coil_no, weight_kg: c.weight_kg })) : [{ coil_no: "", weight_kg: "" }]);
    setIssueSel({}); setShowForm(true);
  };

  const onSave = async () => {
    if (!form.item_id) { toast.error("Select an item"); return; }
    if (!form.party.trim()) { toast.error("Party / ledger is required"); return; }
    if (editing && isOp && !remark.trim()) { toast.error("A remark is required to request this change"); return; }
    try {
      const payload = { ...form, qty: +form.qty, price: +form.price, gst_pct: +form.gst_pct };
      delete payload.id; delete payload.serial_no; delete payload.total_gst; delete payload.total_value; delete payload.item_name; delete payload.unit; delete payload.created_by; delete payload.created_at; delete payload.is_coil; delete payload.coils; delete payload.issue_coils;
      if (isCoilItem) {
        if (form.type === "inward") {
          const coils = coilRows.filter((c) => +c.weight_kg > 0).map((c) => ({ coil_no: c.coil_no || "", weight_kg: +c.weight_kg }));
          if (!coils.length) { toast.error("Add at least one coil with a weight"); return; }
          payload.coils = coils;
        } else {
          const issue = Object.entries(issueSel).filter(([, w]) => +w > 0).map(([coil_id, w]) => ({ coil_id, weight_kg: +w }));
          if (!issue.length) { toast.error("Select at least one coil to issue"); return; }
          payload.issue_coils = issue;
        }
      }
      const cfg = isOp && editing ? { params: { remark } } : {};
      let res;
      if (editing) res = await api.put(`/inward-outward/${editing.id}`, payload, cfg);
      else res = await api.post("/inward-outward", payload);
      const q = queuedMessage(res);
      toast[q ? "info" : "success"](q || (editing ? "Entry updated" : "Entry added"));
      setShowForm(false); load(); loadMasters();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onDelete = async (id, rmk) => {
    try {
      const res = await api.delete(`/inward-outward/${id}`, isOp ? { params: { remark: rmk } } : {});
      const q = queuedMessage(res);
      toast[q ? "info" : "success"](q || "Deleted"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Layout title="Inward / Outward" subtitle="Purchases & issues · linked to inventory, GST & ledgers"
      actions={
        <div className="flex gap-2">
          {isAdmin && <button data-testid="io-settings-btn" onClick={() => setShowSettings(true)} className="bg-white border border-slate-300 hover:border-blue-500 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 flex items-center gap-2"><Settings size={16} /> Settings</button>}
          <button data-testid="io-export-btn" onClick={() => downloadFile("/export/inward-outward", "Inward_Outward.xlsx")} className="bg-white border border-slate-300 hover:border-blue-500 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 flex items-center gap-2"><Download size={16} /> Export</button>
          <button data-testid="io-add-btn" onClick={startNew} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2"><Plus size={16} /> New Entry</button>
        </div>
      }
    >
      <Toaster richColors position="top-right" />
      {isOp && <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-2.5 text-sm" data-testid="io-operator-note">All your entries (new, edit, delete) are sent to an admin for approval.</div>}
      <div className="flex gap-2 mb-4">
        {[["", "All"], ["inward", "Inward"], ["outward", "Outward"]].map(([v, l]) => (
          <button key={v || "all"} data-testid={`io-filter-${v || "all"}`} onClick={() => setFilterType(v)} className={`px-4 py-2 rounded-lg text-sm font-semibold border ${filterType === v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"}`}>{l}</button>
        ))}
      </div>

      <div className="section-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left py-3 px-3">Date</th>
              <th className="text-left py-3 px-3">Type</th>
              <th className="text-left py-3 px-3">Serial</th>
              <th className="text-left py-3 px-3">Voucher</th>
              <th className="text-left py-3 px-3">Party / Ledger</th>
              <th className="text-left py-3 px-3">Item</th>
              <th className="text-right py-3 px-3">Qty</th>
              {canSeePrices && <th className="text-right py-3 px-3">Price</th>}
              {canSeePrices && <th className="text-right py-3 px-3">GST%</th>}
              {canSeePrices && <th className="text-right py-3 px-3">Total Value</th>}
              <th className="text-left py-3 px-3">Person</th>
              <th></th>
            </tr>
          </thead>
          <tbody data-testid="io-table-body">
            {items.length === 0 && <tr><td colSpan="12" className="text-center py-10 text-slate-400">No entries yet.</td></tr>}
            {items.map((g) => (
              <tr key={g.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                <td className="py-2.5 px-3 font-medium whitespace-nowrap">{g.date}</td>
                <td className="py-2.5 px-3">
                  <span className={`stat-pill ${g.type === "inward" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>
                    {g.type === "inward" ? <ArrowDownToLine size={12} /> : <ArrowUpFromLine size={12} />}{g.type}
                  </span>
                </td>
                <td className="py-2.5 px-3 font-mono text-xs">{g.serial_no || "—"}</td>
                <td className="py-2.5 px-3 text-slate-600 text-xs">{g.voucher_no || "—"}{g.voucher_date ? <span className="text-slate-400"> · {g.voucher_date}</span> : null}</td>
                <td className="py-2.5 px-3">{g.party}</td>
                <td className="py-2.5 px-3">{g.item_name}<div className="text-xs text-slate-400">{g.category || ""}</div></td>
                <td className="py-2.5 px-3 text-right font-mono whitespace-nowrap">{g.qty} {g.unit}</td>
                {canSeePrices && <td className="py-2.5 px-3 text-right font-mono">{g.price != null ? `₹${g.price}` : "—"}</td>}
                {canSeePrices && <td className="py-2.5 px-3 text-right font-mono">{g.gst_pct != null ? `${g.gst_pct}%` : "—"}</td>}
                {canSeePrices && <td className="py-2.5 px-3 text-right font-mono font-semibold">{g.total_value != null ? `₹${g.total_value.toLocaleString()}` : "—"}</td>}
                <td className="py-2.5 px-3 text-slate-500">{g.inward_person || "—"}</td>
                <td className="py-2.5 px-3 text-right whitespace-nowrap">
                  <button data-testid={`io-edit-${g.id}`} onClick={() => startEdit(g)} className="text-slate-400 hover:text-blue-600 p-1"><Edit3 size={16} /></button>
                  <ConfirmDelete testId={`io-delete-${g.id}`} requireRemark={isOp} onConfirm={(rmk) => onDelete(g.id, rmk)} title="Delete entry?" description="Linked inventory stock will be restored. This cannot be undone." />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h2 className="font-heading text-xl font-semibold">{editing ? "Edit Entry" : "New Inward / Outward Entry"}</h2>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-4">
              <Field label="Type"><select data-testid="io-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="inp"><option value="inward">Inward (Purchase)</option><option value="outward">Outward (Issue)</option></select></Field>
              <Field label="Entry Date"><input data-testid="io-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="inp" /></Field>
              <Field label="Voucher No. (manual)"><input data-testid="io-voucher-no" value={form.voucher_no} onChange={(e) => setForm({ ...form, voucher_no: e.target.value })} className="inp" placeholder="e.g. INV-2024-101" /></Field>
              <Field label="Voucher Date (manual)"><input data-testid="io-voucher-date" type="date" value={form.voucher_date || ""} onChange={(e) => setForm({ ...form, voucher_date: e.target.value })} className="inp" /></Field>
              <Field label="Party / Ledger">
                <input data-testid="io-party" list="io-party-list" value={form.party} onChange={(e) => setForm({ ...form, party: e.target.value })} className="inp" placeholder="Select or type party name" />
                <datalist id="io-party-list">{parties.map((p) => <option key={p.id} value={p.name} />)}</datalist>
              </Field>
              <Field label="Inward / Issue Person">
                <input data-testid="io-person" list="io-person-list" value={form.inward_person} onChange={(e) => setForm({ ...form, inward_person: e.target.value })} className="inp" placeholder="Select or type person name" />
                <datalist id="io-person-list">{persons.map((p) => <option key={p.id} value={p.name} />)}</datalist>
              </Field>
              <Field label="Category (select first)">
                <select data-testid="io-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value, item_id: "" })} className="inp">
                  <option value="">— All categories —</option>
                  {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="Item (filtered by category)">
                <select data-testid="io-item" value={form.item_id} onChange={(e) => setForm({ ...form, item_id: e.target.value })} className="inp">
                  <option value="">— Select item —</option>
                  {invItems.map((it) => <option key={it.id} value={it.id}>{it.name} ({it.base_unit}, stock {it.current_stock})</option>)}
                </select>
              </Field>
              {isCoilItem && form.type === "inward" && (
                <div className="col-span-2 border border-amber-300 bg-amber-50/50 rounded-lg p-3" data-testid="io-coil-inward">
                  <div className="flex items-center justify-between mb-2"><span className="text-sm font-semibold text-amber-800">Coil details — Coil No. + Weight (kg)</span><span className="text-xs text-slate-600">Total: <b>{coilTotalWeight.toLocaleString()} kg</b></span></div>
                  {coilRows.map((c, idx) => (
                    <div key={idx} className="flex gap-2 mb-2">
                      <input data-testid={`io-coil-no-${idx}`} value={c.coil_no} onChange={(e) => setCoilRows(coilRows.map((x, j) => j === idx ? { ...x, coil_no: e.target.value } : x))} placeholder="Coil No." className="inp" />
                      <input data-testid={`io-coil-wt-${idx}`} type="number" value={c.weight_kg} onChange={(e) => setCoilRows(coilRows.map((x, j) => j === idx ? { ...x, weight_kg: e.target.value } : x))} placeholder="Weight kg" className="inp" />
                      <button type="button" onClick={() => setCoilRows(coilRows.length > 1 ? coilRows.filter((_, j) => j !== idx) : coilRows)} className="text-slate-400 hover:text-red-600 px-2"><X size={16} /></button>
                    </div>
                  ))}
                  <button type="button" data-testid="io-coil-add" onClick={() => setCoilRows([...coilRows, { coil_no: "", weight_kg: "" }])} className="text-blue-600 text-sm font-medium flex items-center gap-1"><Plus size={14} /> Add coil</button>
                </div>
              )}
              {isCoilItem && form.type === "outward" && (
                <div className="col-span-2 border border-amber-300 bg-amber-50/50 rounded-lg p-3" data-testid="io-coil-outward">
                  <div className="flex items-center justify-between mb-2"><span className="text-sm font-semibold text-amber-800">Select coil(s) to issue</span><span className="text-xs text-slate-600">Total: <b>{coilTotalWeight.toLocaleString()} kg</b></span></div>
                  {activeCoils.length === 0 && <div className="text-xs text-slate-500">No active coils available for this item.</div>}
                  {activeCoils.map((c) => (
                    <div key={c.id} className="flex items-center gap-2 mb-2">
                      <input type="checkbox" data-testid={`io-issue-check-${c.id}`} checked={issueSel[c.id] !== undefined} onChange={(e) => { const m = { ...issueSel }; if (e.target.checked) m[c.id] = c.remaining_weight_kg; else delete m[c.id]; setIssueSel(m); }} />
                      <span className="text-sm flex-1">{c.coil_no} · {(c.remaining_weight_kg ?? 0).toLocaleString()} kg left</span>
                      {issueSel[c.id] !== undefined && <input data-testid={`io-issue-wt-${c.id}`} type="number" value={issueSel[c.id]} max={c.remaining_weight_kg} onChange={(e) => setIssueSel({ ...issueSel, [c.id]: e.target.value })} className="inp w-32" placeholder="kg" />}
                    </div>
                  ))}
                </div>
              )}
              {!isCoilItem && <Field label="Quantity"><input data-testid="io-qty" type="number" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} className="inp" /></Field>}
              {canSeePrices && <Field label={isCoilItem ? "Price (₹ / kg)" : "Price (₹ / unit)"}><input data-testid="io-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="inp" /></Field>}
              {canSeePrices && <Field label="GST %"><input data-testid="io-gst" type="number" value={form.gst_pct} onChange={(e) => setForm({ ...form, gst_pct: e.target.value })} className="inp" /></Field>}
              <Field label="Remarks"><input data-testid="io-remarks" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="inp" /></Field>
              {canSeePrices && (
                <div className="col-span-2 bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm grid grid-cols-3 gap-2" data-testid="io-totals-preview">
                  <div>Base: <b>₹{base.toLocaleString()}</b></div>
                  <div>Total GST: <b>₹{gstAmt.toLocaleString()}</b></div>
                  <div>Total Value: <b>₹{totalVal.toLocaleString()}</b></div>
                </div>
              )}
              <div className="col-span-2 bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                {form.type === "inward" ? "Inward will CREDIT" : "Outward will DEBIT"} <b>{isCoilItem ? `${coilTotalWeight.toLocaleString()} kg` : (form.qty || 0)}</b> {form.type === "inward" ? "to" : "from"} the selected item's inventory stock.
              </div>
            </div>
            {editing && isOp && (
              <div className="px-5 pb-2"><label className="block text-sm font-medium text-amber-700 mb-1.5">Reason for edit (sent to admin for approval)</label><textarea data-testid="io-approval-remark" value={remark} onChange={(e) => setRemark(e.target.value)} rows={2} className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm bg-amber-50" /></div>
            )}
            <div className="flex justify-end gap-2 p-5 border-t border-slate-200 bg-slate-50">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 bg-white">Cancel</button>
              <button data-testid="io-save-btn" onClick={onSave} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold">Save</button>
            </div>
          </div>
        </div>
      )}

      {showSettings && <SettingsModal onClose={() => { setShowSettings(false); loadMasters(); }} parties={parties} persons={persons} reload={loadMasters} />}

      <style>{`.inp{width:100%;border:1px solid #cbd5e1;border-radius:.5rem;padding:.5rem .75rem;font-size:.875rem}`}</style>
    </Layout>
  );
}

function SettingsModal({ onClose, parties, persons, reload }) {
  const [settings, setSettings] = useState(null);
  const [newParty, setNewParty] = useState("");
  const [newPerson, setNewPerson] = useState("");
  const [editParty, setEditParty] = useState({ id: null, name: "" });
  const [editPerson, setEditPerson] = useState({ id: null, name: "" });
  useEffect(() => { api.get("/inout-settings").then((r) => setSettings(r.data)); }, []);

  const saveSettings = async () => {
    try {
      await api.put("/inout-settings", {
        inward_prefix: settings.inward_prefix || "", inward_suffix: settings.inward_suffix || "",
        outward_prefix: settings.outward_prefix || "", outward_suffix: settings.outward_suffix || "",
        pad: +settings.pad || 4,
      });
      toast.success("Serial format saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const addParty = async () => { if (!newParty.trim()) return; try { await api.post("/inout-parties", { name: newParty.trim() }); setNewParty(""); reload(); toast.success("Party added"); } catch (e) { toast.error(formatApiError(e)); } };
  const renameParty = async () => { if (!editParty.name.trim()) return; try { await api.put(`/inout-parties/${editParty.id}`, { name: editParty.name.trim() }); setEditParty({ id: null, name: "" }); reload(); toast.success("Party renamed"); } catch (e) { toast.error(formatApiError(e)); } };
  const delParty = async (id) => { try { await api.delete(`/inout-parties/${id}`); reload(); } catch (e) { toast.error(formatApiError(e)); } };
  const addPerson = async () => { if (!newPerson.trim()) return; try { await api.post("/inout-persons", { name: newPerson.trim() }); setNewPerson(""); reload(); toast.success("Person added"); } catch (e) { toast.error(formatApiError(e)); } };
  const renamePerson = async () => { if (!editPerson.name.trim()) return; try { await api.put(`/inout-persons/${editPerson.id}`, { name: editPerson.name.trim() }); setEditPerson({ id: null, name: "" }); reload(); toast.success("Person renamed"); } catch (e) { toast.error(formatApiError(e)); } };
  const delPerson = async (id) => { try { await api.delete(`/inout-persons/${id}`); reload(); } catch (e) { toast.error(formatApiError(e)); } };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl my-8" data-testid="io-settings-modal">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h2 className="font-heading text-xl font-semibold">Inward / Outward Settings</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-6">
          {settings && (
            <div>
              <h3 className="font-semibold text-sm text-slate-800 mb-2">Serial Number Format</h3>
              <p className="text-xs text-slate-500 mb-3">Serial = Prefix + running number (padded) + Suffix. e.g. <b>{settings.inward_prefix}{"0001".slice(-(+settings.pad || 4))}{settings.inward_suffix}</b></p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Inward Prefix"><input data-testid="io-set-in-prefix" value={settings.inward_prefix || ""} onChange={(e) => setSettings({ ...settings, inward_prefix: e.target.value })} className="inp" /></Field>
                <Field label="Inward Suffix"><input data-testid="io-set-in-suffix" value={settings.inward_suffix || ""} onChange={(e) => setSettings({ ...settings, inward_suffix: e.target.value })} className="inp" /></Field>
                <Field label="Outward Prefix"><input data-testid="io-set-out-prefix" value={settings.outward_prefix || ""} onChange={(e) => setSettings({ ...settings, outward_prefix: e.target.value })} className="inp" /></Field>
                <Field label="Outward Suffix"><input data-testid="io-set-out-suffix" value={settings.outward_suffix || ""} onChange={(e) => setSettings({ ...settings, outward_suffix: e.target.value })} className="inp" /></Field>
                <Field label="Number Padding (digits)"><input data-testid="io-set-pad" type="number" value={settings.pad || 4} onChange={(e) => setSettings({ ...settings, pad: e.target.value })} className="inp" /></Field>
              </div>
              <button data-testid="io-set-save" onClick={saveSettings} className="mt-3 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white rounded-lg text-sm font-semibold">Save Serial Format</button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-sm text-slate-800 mb-2">Party / Ledger List</h3>
              <div className="flex gap-2 mb-2">
                <input data-testid="io-new-party" value={newParty} onChange={(e) => setNewParty(e.target.value)} className="inp" placeholder="New party name" />
                <button data-testid="io-add-party" onClick={addParty} className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold whitespace-nowrap">Add</button>
              </div>
              <ul className="space-y-1 max-h-48 overflow-y-auto">
                {parties.map((p) => <li key={p.id} className="flex items-center justify-between text-sm bg-slate-50 rounded px-2 py-1.5 gap-1">
                  {editParty.id === p.id ? (
                    <>
                      <input data-testid={`io-party-rename-${p.id}`} value={editParty.name} autoFocus onChange={(e) => setEditParty({ ...editParty, name: e.target.value })} onKeyDown={(e) => e.key === "Enter" && renameParty()} className="flex-1 border border-blue-400 rounded px-1.5 py-0.5 text-sm" />
                      <button data-testid={`io-party-rename-save-${p.id}`} onClick={renameParty} className="text-emerald-600 hover:text-emerald-700 text-xs font-semibold">Save</button>
                      <button onClick={() => setEditParty({ id: null, name: "" })} className="text-slate-400 hover:text-slate-600"><X size={14} /></button>
                    </>
                  ) : (
                    <>
                      <span>{p.name}</span>
                      <span className="flex items-center gap-1 shrink-0">
                        <button data-testid={`io-party-edit-${p.id}`} onClick={() => setEditParty({ id: p.id, name: p.name })} className="text-slate-400 hover:text-blue-600"><Edit3 size={14} /></button>
                        <button onClick={() => delParty(p.id)} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                      </span>
                    </>
                  )}
                </li>)}
                {parties.length === 0 && <li className="text-xs text-slate-400">No parties yet.</li>}
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-sm text-slate-800 mb-2">Inward / Issue Person List</h3>
              <div className="flex gap-2 mb-2">
                <input data-testid="io-new-person" value={newPerson} onChange={(e) => setNewPerson(e.target.value)} className="inp" placeholder="New person name" />
                <button data-testid="io-add-person" onClick={addPerson} className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold whitespace-nowrap">Add</button>
              </div>
              <ul className="space-y-1 max-h-48 overflow-y-auto">
                {persons.map((p) => <li key={p.id} className="flex items-center justify-between text-sm bg-slate-50 rounded px-2 py-1.5 gap-1">
                  {editPerson.id === p.id ? (
                    <>
                      <input data-testid={`io-person-rename-${p.id}`} value={editPerson.name} autoFocus onChange={(e) => setEditPerson({ ...editPerson, name: e.target.value })} onKeyDown={(e) => e.key === "Enter" && renamePerson()} className="flex-1 border border-blue-400 rounded px-1.5 py-0.5 text-sm" />
                      <button data-testid={`io-person-rename-save-${p.id}`} onClick={renamePerson} className="text-emerald-600 hover:text-emerald-700 text-xs font-semibold">Save</button>
                      <button onClick={() => setEditPerson({ id: null, name: "" })} className="text-slate-400 hover:text-slate-600"><X size={14} /></button>
                    </>
                  ) : (
                    <>
                      <span>{p.name}</span>
                      <span className="flex items-center gap-1 shrink-0">
                        <button data-testid={`io-person-edit-${p.id}`} onClick={() => setEditPerson({ id: p.id, name: p.name })} className="text-slate-400 hover:text-blue-600"><Edit3 size={14} /></button>
                        <button onClick={() => delPerson(p.id)} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                      </span>
                    </>
                  )}
                </li>)}
                {persons.length === 0 && <li className="text-xs text-slate-400">No persons yet.</li>}
              </ul>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-slate-200 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 bg-white">Close</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) { return <div><label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>{children}</div>; }
